import React, { useEffect, useRef, useState } from "react";

const NoteCard = ({
  note,
  canEdit,
  compact = false,
  onUpdate,
  onUploadImage,
  onDelete,
  onDeleteImage,
  modalOnEdit = false,
}) => {
  const [editing, setEditing] = useState(Boolean(note.__autoEdit));
  const [text, setText] = useState(note.description || "");
  const [saving, setSaving] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!editing || !textareaRef.current) return;
    textareaRef.current.style.height = "auto";
    textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
  }, [editing, text]);

  useEffect(() => {
    if (!modalOnEdit) return;
    if (editing) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => document.body.classList.remove("modal-open");
  }, [editing, modalOnEdit]);

  const handleSave = async () => {
    if (!onUpdate) return;
    setSaving(true);
    try {
      await onUpdate(note.id, text);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !onUploadImage) return;
    await onUploadImage(note.id, file);
    e.target.value = "";
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    await onDelete(note.id);
  };

  return (
    <div className="note-card">
      <div className="note-header">
        <img
          className="note-avatar"
          src={note.user?.profilePictureUrl || "/icons/Logo_PNG.png"}
          alt={note.user?.username || "User"}
        />
        <div>
          <div className="note-user">{note.user?.username || "Unknown user"}</div>
          <div className="note-time">
            {note.createdAt ? new Date(note.createdAt).toLocaleString() : ""}
          </div>
        </div>
        {canEdit && !compact && (
          <div className="note-menu">
            <button
              type="button"
              className="note-menu-btn"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Note actions"
            >
              ...
            </button>
            {menuOpen && (
              <div className="note-menu-popover">
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    setEditing(true);
                  }}
                >
                  <span className="note-menu-icon" aria-hidden="true">
                    ✎
                  </span>
                  Edit Note
                </button>
                <button type="button" onClick={handleDelete}>
                  <span className="note-menu-icon" aria-hidden="true">
                    🗑
                  </span>
                  Delete Note
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {!editing && (
        <div className={`note-desc${compact ? " clamp-3" : ""}`}>
          {note.description || "-"}
        </div>
      )}

      {editing && !modalOnEdit && (
        <div className="note-edit">
          <textarea
            ref={textareaRef}
            className="note-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="note-edit-actions">
            <label className="note-upload">
              Add photo
              <input type="file" accept="image/*" onChange={handleUpload} />
            </label>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              Save
            </button>
          </div>
        </div>
      )}

      {editing && modalOnEdit && (
        <div className="note-modal-backdrop">
          <div
            className="note-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="note-modal-header">
              <div className="note-modal-title">Edit Note</div>
              <button
                type="button"
                className="note-modal-close"
                onClick={() => setEditing(false)}
              >
                ✕
              </button>
            </div>
            <div className="note-modal-body">
              <textarea
                ref={textareaRef}
                className="note-textarea"
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              {note.files?.length > 0 && (
                <div className="note-files">
                  {note.files.map((f) => (
                    <div key={f.id} className="note-file note-file-edit">
                      <img src={f.url} alt="Note file" />
                      <button
                        type="button"
                        className="note-file-remove"
                        title="Remove image"
                        onClick={(e) => {
                          e.stopPropagation();
                          const imageId =
                            f.id || f.imageId || f.publicId || f.fileId || "";
                          onDeleteImage && onDeleteImage(note.id, imageId, f.url);
                        }}
                      >
                        X
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="note-modal-actions">
              <label className="note-upload">
                Add photo
                <input type="file" accept="image/*" onChange={handleUpload} />
              </label>
              <div className="note-modal-spacer" />
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setEditing(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {note.files?.length > 0 && (!compact || editing) && (
        <div className="note-files">
          {note.files.map((f) => (
            <div key={f.id} className="note-file note-file-edit">
              <img src={f.url} alt="Note file" />
              {editing && (
                      <button
                        type="button"
                        className="note-file-remove"
                        title="Remove image"
                        onClick={(e) => {
                          e.stopPropagation();
                          const imageId =
                            f.id || f.imageId || f.publicId || f.fileId || "";
                          onDeleteImage && onDeleteImage(note.id, imageId, f.url);
                        }}
                      >
                        X
                      </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NoteCard;
